<template>
  <div class="Scene">
    <canvas
      id="renderCanvas"
      ref="canvas"
      class="Scene-canvas"
      touch-action="none"
      oncontextmenu="return false"
    ></canvas>
  </div>
</template>

<script>
import {
  Engine,
  Scene,
  ArcRotateCamera,
  Vector2,
  Vector3,
  HemisphericLight,
  PointLight,
  Tools,
  HighlightLayer,
  Color3,
  Color4,
  PointerDragBehavior,
  AutoRotationBehavior,
  Action,
  Matrix,
  Mesh,
} from "@babylonjs/core";

import "@babylonjs/loaders/OBJ";
import { AssetsManager } from "@babylonjs/core/Misc/assetsManager";
import { AdvancedDynamicTexture, Button, TextBlock } from "@babylonjs/gui/2D";
import { StackPanel } from "@babylonjs/gui/2D/controls/stackPanel";
import { Control } from "@babylonjs/gui/2D/controls/control";
import { ActionManager } from "@babylonjs/core/Actions/actionManager";
import { ExecuteCodeAction } from "@babylonjs/core/Actions/directActions";
import { DefaultRenderingPipeline } from "@babylonjs/core/PostProcesses/RenderPipeline/Pipelines/defaultRenderingPipeline";
import {
  loadPlateMesh,
  createCylinder,
  loadSymbolAMesh,
  loadSymbolBMesh,
} from "../configurator/lib/assets";
import {
  createPlateMaterial,
  createCylinderMaterial,
} from "../configurator/lib/materials";
import { StatusEnum, xRight, xLeft } from "../shared/constants";
import { mapActions, mapGetters } from "vuex";
import { EventBus } from "../../main";

export default {
  name: "OrderPreview",
  components: {},
  provide() {
    return {
      babylon: this.babylon,
    };
  },
  computed: {
    ...mapGetters(["selectedOrder"]),
  },
  watch: {
    selectedOrder(newOrder, oldOrder) {
      this.resetScene();
      this.previewConfiguration(newOrder);
    },
  },
  created() {
    window.addEventListener("resize", () => this.layout());
  },

  data() {
    return {
      logik: {},
      babylon: {
        scene: null,
        sceneReady: false,
        canvas: null,
      },
      scene: {
        camera: null,
        plateMesh: null,
        cylinderMeshes: [],
        plateMaterial: null,
        cylinderMaterial: null,
        symbolAMesh: null,
        symbolBMesh: null,
      },
    };
  },
  mounted() {
    this.createScene();
    this.babylon.sceneReady = true;
    this.babylon.canvas = this.$refs.canvas;

    this.engine.runRenderLoop(() => {
      this.babylon.scene.render();
    });
  },
  beforeDestroy() {
    this.babylon.scene.dispose();
    this.engine.dispose();
  },
  methods: {
    async createScene() {
      // Create render loop, a camera and some basic lights:
      this.engine = new Engine(
        this.$refs.canvas,
        true,
        { preserveDrawingBuffer: true },
        false
      );
      this.babylon.scene = new Scene(this.engine);
      var camera = new ArcRotateCamera(
        "camera",
        -Math.PI / 2,
        Tools.ToRadians(67),
        350,
        new Vector3(0, 50, 0),
        this.babylon.scene
      );
      this.scene.camera = camera;
      camera.useAutoRotationBehavior = true;
      camera.autoRotationBehavior.idleRotationSpeed = 0.25;
      camera.lowerRadiusLimit = 250;
      camera.upperRadiusLimit = 800;

      camera.attachControl(this.$refs.canvas, true);
      this.scene.highlightLayer = new HighlightLayer("hl1", this.babylon.scene);
      new HemisphericLight("light1", new Vector3(1, 1, 0), this.babylon.scene);

      this.babylon.scene.createDefaultEnvironment({
        createGround: false,
        createSkybox: true,
        skyboxSize: 1500,
        skyboxColor: new Color3(144 / 255, 202 / 255, 249 / 255),
        enableGroundMirror: false,
      });

      var defaultPipeline = new DefaultRenderingPipeline(
        "DefaultRenderingPipeline",
        false, // is HDR?
        this.babylon.scene,
        this.babylon.scene.cameras
      );
      defaultPipeline.samples = 1;
      defaultPipeline.fxaaEnabled = false;

      this.scene.plateMaterial = createPlateMaterial(this.babylon.scene);
      this.scene.cylinderMaterial = createCylinderMaterial(this.babylon.scene);
      var assetLoader = new AssetsManager(this.babylon.scene);
      loadPlateMesh(
        assetLoader,
        (mesh) => (this.scene.plateMesh = mesh),
        this.scene.plateMaterial
      );
      loadSymbolAMesh(assetLoader, (mesh) => {
        this.scene.symbolAMesh = mesh;
        this.scene.symbolAMesh.billboardMode = Mesh.BILLBOARDMODE_ALL;
      });
      loadSymbolBMesh(assetLoader, (mesh) => {
        this.scene.symbolBMesh = mesh;
        this.scene.symbolBMesh.billboardMode = Mesh.BILLBOARDMODE_ALL;
      });
      await assetLoader.loadAsync();
      this.previewConfiguration(this.selectedOrder);
    },

    resetScene() {
      this.scene.cylinderMeshes.forEach((mesh) => {
        mesh.dispose();
      });
    },

    previewConfiguration(selectedOrder) {
      var leftHeight = 0;
      var rightHeight = 0;

      selectedOrder.leftStack.forEach((cylinder) => {
        var current = createCylinder(
          cylinder.height,
          { r: cylinder.color.r, g: cylinder.color.g, b: cylinder.color.b },
          this.scene.cylinderMaterial.clone(),
          this.babylon.scene
        );
        current.position = new Vector3(
          xLeft,
          leftHeight + cylinder.height / 2,
          0
        );
        leftHeight += cylinder.height;
        this.scene.cylinderMeshes.push(current);
      });
      selectedOrder.rightStack.forEach((cylinder) => {
        var current = createCylinder(
          cylinder.height,
          { r: cylinder.color.r, g: cylinder.color.g, b: cylinder.color.b },
          this.scene.cylinderMaterial.clone(),
          this.babylon.scene
        );
        current.position = new Vector3(
          xRight,
          rightHeight + cylinder.height / 2,
          0
        );
        rightHeight += cylinder.height;
        this.scene.cylinderMeshes.push(current);
      });

      this.scene.symbolAMesh.position.y = this.scene.symbolBMesh.position.y =
        Math.max(leftHeight, rightHeight) + 70;
    },

    resetConfigurator() {},
    layout() {
      if (this.engine) this.engine.resize();
    },
  },
};
</script>

<style scoped>
.Scene {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 5px;
  background-color: white;
  overflow: hidden;
}
.Scene-canvas {
  width: 100%;
  height: 100%;
  touch-action: none;
  outline: none;
  display: block;
}
</style>
